import { Component, createRef } from 'react';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ExtensionInfoComponent } from '../../components/ui/extensions-info/extensions-info.component';
import { FeatureDetailsComponent } from '../../components/ui/feature-details/feature-details.component';
import FeaturesTilesComponent from '../../components/ui/features-tiles/features-tiles.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import RatingDisplayComponent from '../../components/ui/rating-display/rating-display.component';
import { RelatedLinksComponent } from '../../components/ui/related-links/related-links.component';
import { IOC_TYPES } from '../../context/ioc.types';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { Logger } from '../../models/logger';
import { DOMStorage } from '../../services/dom-storage.service';
import { EnvironmentService } from '../../services/environment.service';
import { LightHttp } from '../../services/light-http.service';
import { MotivesService } from '../../services/motives.service';
import { PageDataProviderService } from '../../services/page-data-provider.service';
import { SettingsService } from '../../services/settings.service';
import { UploadedFilesService } from '../../services/uploaded-files.service';
import { WindowService } from '../../services/window.service';
import { ExtensionSEOData, PageData, RouteProps, SeoFeature } from '../../types/route-types';
import { FromType, FromTypes, ToType, ToTypes } from '../../types/types';
import { BrowserUtils } from '../../ui-utils/browser-utils';
import { get } from '../../utils/lodash-min';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { RouteUtils } from '../../utils/route.utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import MotiveFrame from '../motive-frames/motive-frame.component';
import styles from './converter-page-base.component.module.scss';
import FileConvertComponent from './file-convert.component';


interface ConverterState {
    fileSelectBtnClicked: boolean;
}

interface ConverterProps extends RouteProps {

}


export default class ConverterPageBaseComponent extends Component<ConverterProps, ConverterState> {
    logger = Logger.getLogger('ConverterPageBaseComponent');

    private subscriptions: Subscription[] = [];

    el = createRef<HTMLDivElement>();
    @lazyInject(EnvironmentService) env: EnvironmentService;
    @lazyInject(LightHttp) lightHttp: LightHttp;
    @lazyInject(DOMStorage) domStorage: DOMStorage;
    @lazyInject(PageDataProviderService) pageDataProvider: PageDataProviderService;
    @lazyInject(MotivesService) motivesService: MotivesService;


    @lazyInject(UploadedFilesService) private uploadedFilesService: UploadedFilesService;
    @lazyInject(MotivesService) public motiveService: MotivesService;
    @lazyInject(SettingsService) private settingsService: SettingsService;
    @lazyInject(WindowService) private windowService: WindowService;

    showTestComponents = false;

    get domainname() {
        return this.env?.domainname;
    }
    get sitename() {
        return this.env?.sitename;
    }

    get showRating() {
        return this.env?.sitename === this.env?.env.constants.xconvertId;
    }

    get showInfoGrid() {
        return this.env?.sitename === this.env?.env.constants.xconvertId;
    }

    // public sitename = 'xconvert';
    public title: string;
    public description: string;
    public features: Array<SeoFeature>;

    public showHTML = false;
    public isMobile: boolean;

    public feature;

    public markdownData;

    sidePanelFileUploads: FileUpload[];
    showSidePanelTriggered = false;
    canShowLivePreview = false;
    showLivePreview = false;

    effectiveMotives: MotiveConfig;
    extensionSeoDataList: ExtensionSEOData[] = [];

    featureDescriptionsDisabled = false;


    get internalAdsEnabled() {
        return this.effectiveMotives && this.effectiveMotives.internalMotivesEnabled;
    }
    get leftAdShown() {
        return this.effectiveMotives && this.effectiveMotives['convertPageLeft'] && !this.internalAdsEnabled;
    }
    get rightAdShown() {
        return this.effectiveMotives && this.effectiveMotives['convertPageRight'] && !this.internalAdsEnabled;
    }

    readonly state: Readonly<ConverterState> = { fileSelectBtnClicked: false };
    constructor(props: any) {
        super(props);
        this.effectiveMotives = this.motiveService.freeUserMotives;
        this.loadComponentData(this.props);
        this.updatePageData(props.pageData);
    }

    componentDidMount() {
        const componentMountedTime = Date.now();
        const fileSelectSub = this.windowService.onFileSelectBtnClicked.pipe(
            filter(evt => evt.dispatchedAt.getTime() > componentMountedTime)
        ).subscribe({
            next: (data) => {
                if (!this.state.fileSelectBtnClicked) {
                    this.setState({
                        fileSelectBtnClicked: false,
                    });
                }
            }
        });
        this.subscriptions.push(fileSelectSub);
    }

    loadComponentData(props) {
        this.feature = RouteUtils.getPathFromProps(props);
        this.isMobile = BrowserUtils.isMobile();
        this.domStorage.setBaseComponentReadyAndFreezeContentIfNeeded();

        this.motivesService.onEffectiveMotiveSettingsChange
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (motives) => {
                    this.effectiveMotives = motives;
                    // Preact disable this until find a better way. For now its going though the route data
                    // this.changeKeyAndForceUpdate();
                },
                error: (error) => {
                    this.logger.error({ message: 'Motive settings change observer threw an error', error });
                    // this.changeKeyAndForceUpdate();
                },
            });


        this.settingsService.userSettings
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (userSettings) => {
                    this.featureDescriptionsDisabled = get(userSettings, 'featureDescriptionsDisabled', false);
                },
            });
        this.settingsService.featureFlags
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (ff) => {
                    this.showLivePreview = get(ff, 'showLivePreview', false);
                },
            });
    }

    shouldComponentUpdate(nextProp: ConverterProps, nextState: ConverterState) {
        const isDifferentURL = nextProp.url !== this.props.url;
        const isFileSelectBtnClickedStateChanged = nextState.fileSelectBtnClicked !== this.state.fileSelectBtnClicked;
        return isDifferentURL || isFileSelectBtnClickedStateChanged;
    }

    private updatePageData(value: PageData) {

        this.features = value.page_seo.feature;
        this.title = value.page_seo.title;
        this.description = value.page_seo.description && value.page_seo.description.replace(/\s+/g, ' ');
        this.uploadedFilesService.initializeForCompatibility(value.fromType, value.accept);
        this.uploadedFilesService.notyfyAll();

        this.updateCanShowLivePreview(value.fromType, value.toType);
    }

    componentWillUnmount() {
        // for untilDestroyed
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    updateCanShowLivePreview(fromType: FromType[], toType: ToType) {
        const notAllowedToTypes = [ToTypes.VIDEO]; // []; //
        const notAllowedFromTypes = [FromTypes.VIDEO, FromTypes.AV]; // []; //
        if (notAllowedToTypes.indexOf(toType) === -1 && !notAllowedFromTypes.find(notAllowedFromType => fromType.indexOf(notAllowedFromType) > -1)) {
            this.canShowLivePreview = true;
        } else {
            this.canShowLivePreview = false;
        }
    }

    public navigateToPage(pageHeaderLink: any) {
        if (pageHeaderLink.type === 'remote') {
            window.location.href = pageHeaderLink.url;
        }
        // TODO Preact fix
        // this.router.navigate([pageHeaderLink.url]);
    }

    getFIleConvertComponentHtml(props: ConverterProps) {
        return <FileConvertComponent {...props} belowToolAd={this.belowToolAd()} aboveToolAd={this.aboveToolAd()}>
        </FileConvertComponent>;
    }



    // shouldComponentUpdate(nextProps, t2) {
    //     if (this.props.path !== nextProps.path) {
    //         this.componentWillUnmount();
    //         this.loadComponentData(nextProps);
    //         this.updateKey();
    //     }
    //     return true;
    // }

    changeKeyAndForceUpdate() {
        this.updateKey();
        this.forceUpdate();
    }

    updateKey() {
        // this.setState({
        //     componentKey: StringUtils.getRandomString('key_'),
        // });
    }

    getRatingValue(rating: any): string {
        const total = (rating.rating_1 || 0) + (rating.rating_2 || 0) + (rating.rating_3 || 0) + (rating.rating_4 || 0) + (rating.rating_5 || 0);
        if (!total) return '4.5';
        return ((1*(rating.rating_1||0) + 2*(rating.rating_2||0) + 3*(rating.rating_3||0) + 4*(rating.rating_4||0) + 5*(rating.rating_5||0)) / total).toFixed(1);
    }

    formatReviewCount(rating: any): string {
        const total = (rating.rating_1 || 0) + (rating.rating_2 || 0) + (rating.rating_3 || 0) + (rating.rating_4 || 0) + (rating.rating_5 || 0);
        return total >= 1000 ? `${(total / 1000).toFixed(1).replace(/\.0$/, '')}K reviews` : `${total} reviews`;
    }

    /**
     * The banner that fills the empty file-list area above the uploader.
     *
     * `.sortable-container` reserves `min-height: 100px` for the staged-file list, and that
     * space is empty on every first visit -- a measured 128px hole between the description and
     * the drop zone on a phone. A 320x100 unit rendered *inside* that container occupies space
     * the layout was already spending, so the uploader does not move: putting it above the
     * container instead pushed the drop zone from y=363 to y=483, which is the exact harm an
     * ad above the tool normally does.
     *
     * Only while nothing is staged; the moment there is work in progress the list needs the
     * room back. docs/proposed/AD_PLACEMENT_DESIGN.md 9
     */
    aboveToolAd() {
        return <MotiveComponent
            data={AdSlots.pageTop}
            containerWidth="100%"
            containerHeight="100px"
            location={MotiveLocationTypes.convertPageTop}
            adWidth="320px"
            adHeight="100px"
            showAds={true}
            className='width_major'
        ></MotiveComponent>;
    }

    /**
     * The ad that sits directly under the uploader, rendered here and handed down.
     *
     * Never above the tool -- that placement pushed the thing the visitor came for down the
     * page, which is the leak this workstream exists to close. But "after the whole tool
     * container" put it below the Advanced Options accordion, and on a phone that makes the
     * container 1,168px tall, so the first ad landed at y=1,629 -- nearly two screens down, and
     * a mobile visitor saw none at all.
     *
     * The location key keeps its old name so the server-side kill switch in settings.motive
     * keeps working without a migration. docs/proposed/AD_PLACEMENT_DESIGN.md 9
     */
    belowToolAd() {
        return <div className="feature-below-tool d-flex justify-content-center mt-3">
            <MotiveComponent
                data={AdSlots.belowTool}
                containerWidth="100%"
                containerHeight="280px"
                location={MotiveLocationTypes.convertPageBelowFeatureTitle}
                adWidth="336px"
                adHeight="280px"
                showAds={true}
                className='width_major_md'
            ></MotiveComponent>
        </div>;
    }

    getSEOContent() {
        return <div className={`row ${styles['seo-container']}`}>
            {
                <div className="col-12 mt-3" >
                    <FeatureDetailsComponent markdown={this.props.markdown}></FeatureDetailsComponent>
                </div>
            }
            {/*
              * The only ad inside the SEO body, at the boundary after the long-form content
              * rather than spliced into the prose.
              *
              * Measured on prod: a converter page runs to ~7,500px and Auto Ads leaves
              * everything between the tool and the footer empty -- thousands of pixels a reader
              * scrolls past with nothing in them. This is the premium, non-blocking half of the
              * page: the visitor has finished their job by the time they reach it, so an ad here
              * costs no task time, and it is roughly 3,000px clear of the units above and below
              * it.
              *
              * Ships dark: `convertPageInContent` is absent from the settings document, so it
              * resolves falsy. Enable it only once Auto Ads is scoped off these URLs.
              * docs/proposed/AD_PLACEMENT_DESIGN.md 8.
              */}
            <div className="col-12 mt-3 d-flex justify-content-center">
                <MotiveComponent
                    data={AdSlots.inContent}
                    containerWidth="100%"
                    containerHeight="90px"
                    location={MotiveLocationTypes.convertPageInContent}
                    adWidth="970px"
                    adHeight="90px"
                    showAds={true}
                    className='width_major'
                ></MotiveComponent>
            </div>
            {
                <div className="col-12 mt-3" >
                    <RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent>
                </div>
            }
            {
                this.showRating && <div className="col-12 mt-3" >
                    <RatingDisplayComponent
                        rating={this.props.rating}
                        title={this.title}
                        feature={this.feature}
                        description={this.description}>
                    </RatingDisplayComponent>
                </div>
            }
            {
                this.showInfoGrid && !this.featureDescriptionsDisabled && <div className="col-12 mt-3">
                    {
                        this.features && <FeaturesTilesComponent features={this.features}></FeaturesTilesComponent>
                    }
                </div>
            }
            {
                <div className="col-12 mt-3" >
                    <ExtensionInfoComponent {...this.props}></ExtensionInfoComponent>
                </div>
            }
        </div>;
    }

    render() {
        return <MotiveFrame>
            <div className={styles['wrapper']}>
                <div className={styles['main-card']}>
                    <div className={`${styles['convert-page']} ${this.state.fileSelectBtnClicked ? styles['file-selected'] : ''}`}>
                        <div className={styles['feature-details']}>
                            <h1 className={`${styles['title']} text-center`}>
                                {this.title}
                            </h1>
                            <div className={`${styles['description']} secondary-text text-center`}
                                dangerouslySetInnerHTML={{ __html: this.description }}></div>
                        </div>
                        <div className={styles['tool-container']}>
                            {
                                this.getFIleConvertComponentHtml(this.props)
                            }
                        </div>
                        {
                            this.getSEOContent()
                        }
                        <div className="feature-above-footer d-flex justify-content-center">
                            <MotiveComponent
                                data={AdSlots.aboveFooter}
                                containerWidth="100%"
                                containerHeight="90px"
                                location={MotiveLocationTypes.convertPageBottom}
                                adWidth="970px"
                                adHeight="90px"
                                showAds={true}
                                className='width_major'
                            ></MotiveComponent>
                        </div>
                    </div>
                </div>
            </div>
        </MotiveFrame>
    }
}
